import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PasswordInput from "@/components/PasswordInput";
import { securityQa } from "@/lib/securityQa";
import { preflightResearchInvite } from "@/lib/admin";
import { Loader2, FlaskConical } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || "";

  // Research invite state: null while we haven't preflighted yet;
  // { valid, status, email_hint, label } once fetched. Invalid tokens
  // (expired/used/revoked) still render a small note but the register
  // flow proceeds as a normal signup — no consent stamp.
  const [invite, setInvite] = useState(null);
  useEffect(() => {
    if (!inviteToken) return;
    preflightResearchInvite(inviteToken).then((data) => {
      setInvite(data || { valid: false, status: "missing" });
      // If admin left an email hint on the invite, prefill it.
      if (data?.email_hint) setEmail(data.email_hint);
    });
    // Only re-run if the token itself changes (setEmail is stable via
    // hook identity, we handle it inside the effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken]);

  // Two-step: 0 = basics, 1 = security questions
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // 5 sampled questions + the user's answers, indexed by position
  const [questions, setQuestions] = useState(null); // [{id, text}]
  const [answers, setAnswers] = useState(["", "", "", "", ""]);

  useEffect(() => {
    if (step !== 1 || questions) return;
    securityQa
      .sample()
      .then((res) => setQuestions(res.questions))
      .catch(() => setError("Couldn't load security questions. Refresh and try again."));
  }, [step, questions]);

  const goNext = (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setStep(1);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!questions) return;
    const trimmed = answers.map((a) => (a || "").trim());
    if (trimmed.some((a) => a.length < 2)) {
      setError("Each answer must be at least 2 characters.");
      return;
    }
    const qa = questions.map((q, i) => ({ question_id: q.id, answer: trimmed[i] }));
    setBusy(true);
    try {
      // Pass invite_token through if we have a valid one; the backend
      // silently ignores invalid/used tokens so it's safe to always send.
      const tokenToSend = invite?.valid ? inviteToken : undefined;
      await register(email.trim().toLowerCase(), password, name.trim() || undefined, qa, tokenToSend);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message || "Registration failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 bg-[var(--bg)] text-[var(--text)]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase text-[var(--text-muted)] mb-4">
            Humane Behavior AI
          </h1>
          <p className="font-serif italic text-3xl leading-tight">
            {step === 0 ? "Create an account." : "Secure your account."}
          </p>
          <p className="font-sans text-sm text-[var(--text-muted)] mt-3">
            {step === 0
              ? "For people who already know the textbook answer isn't enough."
              : "Answer all 5. Two random ones will be checked if you ever need to reset your password."}
          </p>
          <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)] mt-4">
            Step {step + 1} of 2
          </div>
        </div>

        {/* Research-invite banner. Shows only when the URL carries a
            valid ?invite=... token. Makes the consent bargain explicit
            so users know their conversations will be used for corpus /
            research when they sign up through this link. */}
        {invite && step === 0 && (
          <div
            data-testid="research-invite-banner"
            className={`mb-6 border p-4 flex gap-3 items-start ${
              invite.valid
                ? "border-[var(--accent)] bg-[color:rgb(255_255_255_/_0.04)]"
                : "border-[var(--border)] bg-transparent"
            }`}
          >
            <FlaskConical
              size={16}
              className={invite.valid ? "text-[var(--accent)] mt-0.5" : "text-[var(--text-dim)] mt-0.5"}
            />
            <div className="text-sm leading-relaxed">
              {invite.valid ? (
                <>
                  <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--accent)] mb-1">
                    Research invite
                  </div>
                  <p className="text-[var(--text)]">
                    You&apos;ve been invited to Humane Behavior AI as a test user
                    {invite.label ? <> for the <span className="italic">{invite.label}</span> cohort</> : null}.
                    By registering through this link, you agree that your
                    conversations may be used for research, product
                    improvement, and training the underlying model. You can
                    review or delete individual conversations at any time
                    from the History drawer.
                  </p>
                </>
              ) : (
                <p className="text-[var(--text-dim)]">
                  This invite is <span className="text-[var(--text)]">{invite.status}</span> —
                  it&apos;s no longer valid. You can still create a normal account below.
                </p>
              )}
            </div>
          </div>
        )}

        {step === 0 ? (
          <form onSubmit={goNext} className="space-y-5" data-testid="register-form">
            <GoogleSignInButton label="Sign up with Google" testid="register-google-btn" />

            <div className="flex items-center gap-3 py-1" aria-hidden="true">
              <span className="flex-1 h-px bg-[var(--border)]" />
              <span className="font-mono-ui text-[9px] tracking-[0.3em] uppercase text-[var(--text-dim)]">
                or with email
              </span>
              <span className="flex-1 h-px bg-[var(--border)]" />
            </div>

            <label className="block">
              <span className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-dim)]">
                Name (optional)
              </span>
              <input
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name"
                className="mt-2 w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 text-base transition-colors"
              />
            </label>

            <label className="block">
              <span className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-dim)]">
                Email
              </span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email"
                className="mt-2 w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 text-base transition-colors"
              />
            </label>

            <label className="block">
              <span className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-dim)]">
                Password (min 8)
              </span>
              <PasswordInput
                required
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password"
                eyeTestId="register-password-toggle"
                wrapperClassName="mt-2"
                className="text-base"
              />
            </label>

            {error && (
              <p
                data-testid="register-error"
                className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="register-next"
              className="w-full py-3 mt-2 font-mono-ui text-[11px] tracking-[0.3em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] transition-colors"
            >
              Continue
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="register-qa-form">
            <div className="bg-[var(--bg-elevated)] border border-[var(--border)] p-4 mb-2">
              <p className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--accent)] mb-2">
                Important — this is the only way to recover your password
              </p>
              <p className="font-ui text-xs text-[var(--text-muted)] leading-relaxed">
                We do <strong className="text-[var(--text)]">not</strong> send password reset
                emails. If you forget your password, you&apos;ll be asked 2 of these
                5 questions chosen at random. Pick answers you&apos;ll remember in 6 months.
              </p>
            </div>

            {!questions ? (
              <p className="text-center font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
                <Loader2 size={11} className="inline animate-spin mr-2" />
                Loading questions…
              </p>
            ) : (
              questions.map((q, i) => (
                <label className="block" key={q.id}>
                  <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
                    Q{i + 1}. {q.text}
                  </span>
                  <input
                    type="text"
                    required
                    minLength={2}
                    maxLength={120}
                    value={answers[i]}
                    onChange={(ev) => {
                      const next = [...answers];
                      next[i] = ev.target.value;
                      setAnswers(next);
                    }}
                    data-testid={`register-answer-${i}`}
                    autoComplete="off"
                    className="mt-2 w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 text-sm transition-colors"
                  />
                </label>
              ))
            )}

            {error && (
              <p
                data-testid="register-error"
                className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3"
              >
                {error}
              </p>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setStep(0);
                  setError("");
                }}
                data-testid="register-back"
                className="flex-1 py-3 font-mono-ui text-[11px] tracking-[0.3em] uppercase border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] hover:border-[var(--text)] transition-colors disabled:opacity-40"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={busy || !questions}
                data-testid="register-submit"
                className="flex-[2] py-3 font-mono-ui text-[11px] tracking-[0.3em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={12} className="animate-spin" />}
                {busy ? "Creating…" : "Create account"}
              </button>
            </div>
          </form>
        )}

        <p className="font-sans text-sm text-[var(--text-muted)] text-center mt-8">
          Already have an account?{" "}
          <Link to="/login" data-testid="register-to-login" className="text-[var(--accent)] hover:underline">
            Sign in.
          </Link>
        </p>

        <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] text-center mt-6">
          Need help?{" "}
          <a
            href="mailto:support@humanebehavior.com"
            data-testid="support-email"
            className="hover:text-[var(--accent)] transition-colors"
          >
            support@humanebehavior.com
          </a>
        </p>

        <p className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] text-center mt-3">
          By creating an account you agree to our{" "}
          <Link to="/terms" data-testid="register-terms-link" className="hover:text-[var(--accent)] transition-colors">
            Terms
          </Link>
          {" · "}
          <Link to="/privacy" data-testid="register-privacy-link" className="hover:text-[var(--accent)] transition-colors">
            Privacy
          </Link>
        </p>
      </div>
    </div>
  );
}
